import React, { useState } from 'react';
import { ItineraryItem, ItemType, ItemStatus } from '../../types/trip';
import { useDesign } from '../../designs';
import { addItineraryItem, updateItineraryItem } from '../../services/itinerary';
import Modal from '../../components/ui/Modal';
import PlacesAutocomplete from '../../components/ui/PlacesAutocomplete';
import ActivityTimePicker from '../../components/ui/ActivityTimePicker';
import DesignButton from '../../components/ui/DesignButton';
import { formatDayOption } from '../../utils/dates';

interface AddItemModalProps {
  tripId: string;
  tripDays: string[];
  existingItem?: ItineraryItem;
  currentUid: string;
  onClose: () => void;
}

export default function AddItemModal({ tripId, tripDays, existingItem, currentUid, onClose }: AddItemModalProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const isEditing = !!existingItem;

  const [type, setType] = useState<ItemType>(existingItem?.type || 'dining');
  const [name, setName] = useState(existingItem?.name || '');
  const [day, setDay] = useState(existingItem?.day || '');
  const [time, setTime] = useState(existingItem?.time || '');
  const [endTime, setEndTime] = useState(existingItem?.endTime || '');
  const [address, setAddress] = useState(existingItem?.address || '');
  const [activityVenue, setActivityVenue] = useState(existingItem?.venue || '');
  const [notes, setNotes] = useState(existingItem?.notes || '');
  const [url, setUrl] = useState(existingItem?.url || '');
  const [lat, setLat] = useState<number | undefined>(existingItem?.lat);
  const [lng, setLng] = useState<number | undefined>(existingItem?.lng);
  const [status, setStatus] = useState<ItemStatus>(existingItem?.status || 'confirmed');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const isDining = type === 'dining';

  // Late-night warning: if the time is 00:00-03:59 AND the selected day is
  // the first day of the trip, the item would render on a non-existent
  // "night before" — block save until a later day is chosen. Handled for
  // day 2+ by the existing late-night display logic in ItineraryTab.
  const lateNightHour = time ? parseInt(time.split(':')[0] || '', 10) : null;
  const isLateNight = lateNightHour != null && lateNightHour >= 0 && lateNightHour < 4;
  const isFirstDay = !!day && tripDays.length > 0 && day === tripDays[0];
  const lateNightOnFirstDay = isLateNight && isFirstDay;

  const handleSubmit = async () => {
    if (!name.trim()) return;
    if (lateNightOnFirstDay) return;
    setIsSubmitting(true);
    setSubmitError('');
    // Venue field only applies to activities — dining uses name as the venue.
    // On edit, include venue unconditionally (empty string clears stale value).
    const venueToSave = !isDining ? activityVenue.trim() : '';
    const itemData = {
      name: name.trim(), type, status,
      ...(day ? { day } : {}),
      ...(time ? { time } : {}),
      ...(endTime ? { endTime } : {}),
      ...(isEditing || venueToSave ? { venue: venueToSave } : {}),
      ...(address ? { address } : {}),
      ...(isEditing || notes ? { notes: notes.trim() } : {}),
      ...(isEditing || url ? { url: url.trim() } : {}),
      ...(lat != null ? { lat } : {}),
      ...(lng != null ? { lng } : {}),
    };
    try {
      if (isEditing && existingItem) {
        await updateItineraryItem(tripId, existingItem.itemId, itemData);
      } else {
        await addItineraryItem(tripId, { ...itemData, addedBy: currentUid });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save item:', err);
      setSubmitError('Failed to save — please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px',
    fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400,
    color: d.textPrimary, backgroundColor: d.bgSurface,
    border: '1px solid rgba(200,146,58,0.10)', borderRadius: '2px',
    outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: "'Raleway', sans-serif",
    fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px',
    textTransform: 'uppercase', color: d.textSecondary, marginBottom: '6px',
  };

  return (
    <Modal title={isEditing ? 'Edit Item' : 'Add to Itinerary'} onClose={onClose}>
      {/* Type segmented control */}
      <div style={{
        display: 'flex', border: '1px solid rgba(200,146,58,0.15)',
        borderRadius: '2px', overflow: 'hidden', marginBottom: '16px',
      }}>
        {(['dining', 'activity'] as ItemType[]).map((t) => {
          const active = type === t;
          return (
            <button key={t} type="button" onClick={() => setType(t)} style={{
              flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 600,
              letterSpacing: '1.5px', textTransform: 'uppercase', textAlign: 'center',
              backgroundColor: active ? 'rgba(200,146,58,0.12)' : 'transparent',
              color: active ? d.accent : d.textSecondary,
            }}>{t === 'dining' ? 'Dining' : 'Activity'}</button>
          );
        })}
      </div>

      {/* === DINING MODE === */}
      {isDining ? (
        <>
          {/* 1. Venue (autocomplete) */}
          <PlacesAutocomplete
            label="Venue"
            value={name}
            onChange={setName}
            onPlaceSelect={(place) => {
              if (place.address) setAddress(place.address);
              if (place.lat) setLat(place.lat);
              if (place.lng) setLng(place.lng);
            }}
          />
          {/* 2. Address (always visible) */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Address</label>
            <input type="text" value={address} placeholder="Street address"
              onChange={(e) => setAddress(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
              style={inputStyle} />
          </div>
        </>
      ) : (
        <>
          {/* 1. Activity Name (plain text) */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Activity Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
              style={inputStyle} />
          </div>
          {/* 2. Venue (autocomplete — search to populate address) */}
          <PlacesAutocomplete
            label="Venue"
            value={activityVenue}
            onChange={setActivityVenue}
            onPlaceSelect={(place) => {
              if (place.address) setAddress(place.address);
              if (place.lat) setLat(place.lat);
              if (place.lng) setLng(place.lng);
            }}
          />
          {/* 3. Address (always visible, auto-filled from Places) */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Address</label>
            <input type="text" value={address} placeholder="Street address"
              onChange={(e) => setAddress(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
              style={inputStyle} />
          </div>
        </>
      )}

      {/* Day + Start Time side by side */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Day</label>
          <select value={day} onChange={(e) => setDay(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
            style={inputStyle}>
            <option value="">—</option>
            {tripDays.map((d) => <option key={d} value={d}>{formatDayOption(d)}</option>)}
          </select>
        </div>
        <ActivityTimePicker label="Start Time" value={time} onChange={(val) => {
          setTime(val);
          if (endTime && val && endTime <= val) setEndTime('');
        }} />
      </div>

      {/* Late-night first-day warning */}
      {lateNightOnFirstDay && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 400,
          color: d.accent, margin: '0 0 12px', lineHeight: 1.4,
        }}>
          Late-night plans (before 4 AM) show under the night before — choose day 2 or later.
        </p>
      )}

      {/* End Time (optional) */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <ActivityTimePicker label="End Time (optional)" value={endTime} onChange={setEndTime} />
        <div style={{ flex: 1 }} />
      </div>

      {/* Link / URL (optional) */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Link</label>
        <input type="url" value={url} placeholder="https://"
          onChange={(e) => setUrl(e.target.value)}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={inputStyle} />
      </div>

      {/* Notes */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
      </div>

      {/* Status segmented control — Confirmed/Considering */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '16px',
      }}>
        {(['confirmed', 'considering'] as ItemStatus[]).map((s) => {
          const active = status === s;
          // Direct hex values — Safari iOS has rendered token-derived
          // colors incorrectly (light teal instead of emerald) when the
          // button also has default -webkit-appearance applied.
          const activeBg = s === 'confirmed' ? '#2A9068' : '#C8923A';
          return (
            <button key={s} type="button" onClick={() => setStatus(s)} style={{
              flex: 1, padding: '10px 0', cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase', textAlign: 'center',
              borderRadius: '2px',
              border: active ? `1px solid ${activeBg}` : `1px solid ${d.textSecondary}`,
              backgroundColor: active ? activeBg : 'transparent',
              color: active ? '#EDE4D8' : d.textSecondary,
              WebkitAppearance: 'none', appearance: 'none',
            }}>{s === 'confirmed' ? 'Confirmed' : 'Considering'}</button>
          );
        })}
      </div>

      {submitError && (
        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.negative, marginBottom: '8px', textAlign: 'center' }}>{submitError}</p>
      )}

      <div style={{ marginTop: '8px' }}>
        <DesignButton tier={2} fullWidth onClick={handleSubmit} disabled={isSubmitting || !name.trim() || lateNightOnFirstDay}>
          {isSubmitting ? 'Saving...' : isEditing ? 'Save' : '◆ ADD ◆'}
        </DesignButton>
      </div>
    </Modal>
  );
}
